import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, onSnapshot } from 'firebase/firestore';

/**
 * 화면에 보여줄 '지금 버전'을 개발 메모(E2B_DEV_MEMOS)에서 실시간으로 읽어 온다. (2026-09-23)
 *
 * PC 상단 바에만 있던 코드를 밖으로 뺐다.
 * 모바일 화면은 버전이 코드에 박혀 있어서(E2B V2.4) PC 가 V3.16 을 가리키는 동안에도
 * 옛날 번호를 계속 보여줬다. 두 화면이 같은 함수를 쓰게 해서 다시 어긋나지 않게 한다.
 */
export default function useAppVersion() {
  const [version, setVersion] = useState('V2.2');
  const [date, setDate] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'E2B_DEV_MEMOS'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (snapshot.empty) return;
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // 어떤 메모가 '최신'인가 — 날짜를 먼저 본다. (2026-09-21)
      // 예전에는 버전 문자열만 숫자 비교했는데, 번호를 하나만 잘못 적어도(예: v3.9 를 v3.90 으로)
      // 그 메모가 영원히 최신으로 잡혀 화면 버전이 엉뚱하게 굳어버렸다.
      const toTime = (m) => {
        if (m.releaseDate) {
          const t = Date.parse(m.releaseDate);
          if (!isNaN(t)) return t;
        }
        if (m.createdAt && typeof m.createdAt.toDate === 'function') return m.createdAt.toDate().getTime();
        return 0;
      };
      const versioned = data.filter(m => m.version);
      versioned.sort((a, b) => {
        const byDate = toTime(b) - toTime(a);
        if (byDate !== 0) return byDate;
        // 날짜가 같거나 없으면 그때만 버전 번호로 비교한다
        return String(b.version).localeCompare(String(a.version), undefined, { numeric: true, sensitivity: 'base' });
      });
      const latest = versioned[0];
      if (latest && latest.version) setVersion(String(latest.version).toUpperCase());
      if (latest && latest.releaseDate) {
        setDate(latest.releaseDate);
      } else if (latest && latest.createdAt && typeof latest.createdAt.toDate === 'function') {
        const d = latest.createdAt.toDate();
        setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
      }
    }, (error) => console.error('버전 로드 오류:', error));
    return () => unsubscribe();
  }, []);

  // 브라우저 탭 제목도 같은 버전을 따라가게 한다 (2026-09-23)
  // 예전에는 index.html 에 'EZBILITY CRM V1.0' 이 박혀 있어서, 회사 표기도 틀리고
  // 버전도 실제(V3.16)와 한참 어긋난 채로 탭·즐겨찾기·홈 화면 이름에 남아 있었다.
  useEffect(() => {
    document.title = `E2Bility CRM ${version}`;
  }, [version]);

  // PC·모바일이 똑같이 쓰는 표시 문구 (예: "V3.16 (2026-09-21)")
  return { version, date, label: date ? `${version} (${date})` : version };
}
